/* eslint-disable no-underscore-dangle */
import Ajv from 'ajv';
import { JsonEditorProps } from 'json-edit-react';

import {
  GetLiveContentForContentParametersQuery,
  MobileArticleTreatment,
  PageModule,
  ParameterRange,
  Visual,
  ZoneNames
} from 'data/generated/graphql';
import { AuraEmployeeResponse } from 'hooks/useAuraQuery';
import { getTreatmentType } from 'utils/modules';
import { SIZES, TREATMENTS, VISUAL } from './constants';

export const getArtifactName = (
  result: GetLiveContentForContentParametersQuery | undefined,
  allessehId: string | undefined
): string => {
  let pageName = allessehId ?? 'UNKNOWN';

  if (!result || result.liveContentByAllessehId.length === 0) {
    return pageName;
  }

  if (result.liveContentByAllessehId[0].body.__typename === 'PageDTO') {
    pageName = result.liveContentByAllessehId[0].body.metadata.name;
  }

  return pageName;
};

export const getSortedTreatmentList = (result: GetLiveContentForContentParametersQuery | undefined): string[] => {
  const sortedSet = new Set<string>();

  if (!result || result.liveContentByAllessehId.length === 0) {
    return [];
  }

  if (result.liveContentByAllessehId[0].body.__typename === 'PageDTO') {
    const pageDTO = result.liveContentByAllessehId[0].body;
    const modules = pageDTO.root.collection[0].collection;

    modules.forEach((module) => {
      if (module.__typename === 'ModuleContainer') {
        const treatmentType = getTreatmentType(module.attributes.pageModule as PageModule);
        if (treatmentType != null) sortedSet.add(treatmentType);
      }
    });
  }

  return [...sortedSet];
};

export const isValidRange = (ranges: ParameterRange[]) => {
  const errors: string[] = [];

  if (!Array.isArray(ranges) || ranges.length === 0) {
    errors.push('Parameters must be a non-empty array.');
    return errors;
  }

  // Check first range starts at 0
  if (ranges[0].start !== 0) {
    errors.push('start of first item must be 0.');
  }

  // Validate each range
  for (let i = 0; i < ranges.length; i++) {
    const curr = ranges[i];
    const isLast = i === ranges.length - 1;

    // Check if end is null for non-last items
    if (!isLast && typeof curr.end !== 'number') {
      errors.push(`end of item ${i} must be a number (only the last item can have null end).`);
    }

    // Check if end is not null for last item
    if (isLast && curr.end !== null) {
      errors.push('end of last item must be null.');
    }

    // Check if end is greater than or equal to start (for numeric end values)
    if (typeof curr.start === 'number' && typeof curr.end === 'number') {
      if (curr.end < curr.start) {
        errors.push(
          `end of item ${i} (${curr.end}) must be greater than or equal to start of item ${i} (${curr.start}).`
        );
      }
    }

    // Check consecutive ranges - ensure no gaps and no overlaps
    if (!isLast && typeof curr.end === 'number') {
      const next = ranges[i + 1];

      if (next.start !== curr.end + 1) {
        if (next.start > curr.end + 1) {
          errors.push(
            `gap detected: item ${i + 1} starts at ${next.start} but should start at ${
              curr.end + 1
            } (one after end of item ${i}).`
          );
        } else {
          errors.push(
            `overlap detected: item ${i + 1} starts at ${next.start} but item ${i} ends at ${curr.end}. Item ${
              i + 1
            } should start at ${curr.end + 1}.`
          );
        }
      }
    }
  }

  return errors;
};

export const ON_ADD: JsonEditorProps['onAdd'] = ({ currentData }) => {
  const data = currentData as ParameterRange[];

  const lastItem = data.at(-1);

  let lastItemStart = null;
  let lastItemEnd = null;

  if (lastItem && typeof lastItem.end === 'number') {
    lastItemStart = lastItem.end + 1;
    lastItemEnd = lastItemStart + 1;
  }

  if (lastItem === undefined) {
    lastItemStart = 0;
  }

  return [
    'value',
    [
      ...data,
      {
        start: lastItemStart,
        end: lastItemEnd,
        value: {
          size: lastItem?.value.size ?? ZoneNames.S,
          show_flashline: lastItem?.value.show_flashline ?? false,
          treatment: lastItem?.value.treatment ?? MobileArticleTreatment.StandardNews,
          visual: lastItem?.value.visual ?? Visual.Primary,
          show_summary: lastItem?.value.show_summary ?? false
        }
      }
    ]
  ];
};

export const EDITOR_ENUMS: JsonEditorProps['restrictTypeSelection'] = ({ key }) => {
  if (key === 'treatment') {
    return [
      {
        enum: 'Treatments',
        values: TREATMENTS,
        matchPriority: 1
      }
    ];
  }

  if (key === 'size') {
    return [
      {
        enum: 'Sizes',
        values: SIZES,
        matchPriority: 1
      }
    ];
  }

  if (key === 'visual') {
    return [
      {
        enum: 'Visual',
        values: VISUAL,
        matchPriority: 1
      }
    ];
  }

  if (key === 'show_flashline') {
    return ['boolean'];
  }

  if (key === 'show_summary') {
    return ['boolean'];
  }

  if (key === 'start') {
    return ['number', 'null'];
  }

  if (key === 'end') {
    return ['number', 'null'];
  }

  return false;
};

export function getUserText(userData: AuraEmployeeResponse | undefined, userId: string) {
  return userData ? `${userData.first_name ?? ''} ${userData.last_name ?? ''}` : userId;
}

const ajv = new Ajv({ allErrors: true });

const PARAMETER_RANGE_SCHEMA = {
  type: 'array',
  items: {
    type: 'object',
    properties: {
      start: { type: 'integer', minimum: 0, maximum: 999 },
      end: { oneOf: [{ type: 'integer', minimum: 0, maximum: 999 }, { type: 'null' }] },
      value: {
        type: 'object',
        properties: {
          show_flashline: { type: 'boolean' },
          show_summary: { type: 'boolean' },
          size: { enum: SIZES },
          treatment: { enum: TREATMENTS },
          visual: { enum: VISUAL },
          display_type: { type: 'string' },
          red_flashline: { type: 'boolean' },
          center_text: { type: 'boolean' }
        },
        required: ['show_flashline', 'show_summary', 'size', 'treatment', 'visual'],
        additionalProperties: false
      }
    },
    required: ['start', 'value'],
    additionalProperties: false
  }
};

export const validate = ajv.compile(PARAMETER_RANGE_SCHEMA);
